import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Centred "nothing here / something happened" panel, the shared shape of
 * boards 10 (Check your inbox), 11 (Expired link) and 12 (404):
 *
 *   media      → a 74px surface circle with a 1px line and an accent icon
 *                (`icon`), or any custom node (`media`, e.g. the big "404")
 *   title      → Fraunces. `size="page"` 42/600 (32 on mobile) as the page h1;
 *                `size="section"` 23/500 for an empty list inside a page
 *   description→ 15.5 muted, lh 1.6, max ~540px
 *   children   → actions (forms, buttons, link rows), centred below
 *
 * Use `titleAs` to keep the heading outline right (h1 on a full page, h2/h3
 * inside a section).
 */
export interface EmptyStateProps {
  icon?: ReactNode;
  /** Replaces the icon circle entirely (e.g. the 404 numeral). */
  media?: ReactNode;
  title: ReactNode;
  titleAs?: 'h1' | 'h2' | 'h3';
  description?: ReactNode;
  size?: 'page' | 'section';
  className?: string;
  children?: ReactNode;
  /**
   * Move focus to the heading on mount (it gets `tabIndex={-1}`). Use when the
   * panel replaces the previous screen after a submit, so focus doesn't drop
   * to <body> and screen readers announce the new screen.
   */
  autoFocusTitle?: boolean;
}

export function EmptyState({
  icon,
  media,
  title,
  titleAs,
  description,
  size = 'page',
  className,
  children,
  autoFocusTitle = false,
}: EmptyStateProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (autoFocusTitle) headingRef.current?.focus();
  }, [autoFocusTitle]);
  const Heading = titleAs ?? (size === 'page' ? 'h1' : 'h2');
  const page = size === 'page';
  return (
    <div className={cn('mx-auto flex w-full max-w-[560px] flex-col items-center text-center', className)}>
      {media ??
        (icon && (
          <div
            aria-hidden="true"
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full border border-sl-line bg-sl-surface text-sl-accent',
              page ? 'h-[74px] w-[74px] [&_svg]:h-7 [&_svg]:w-7' : 'h-14 w-14 [&_svg]:h-6 [&_svg]:w-6'
            )}
          >
            {icon}
          </div>
        ))}
      <Heading
        ref={headingRef}
        tabIndex={autoFocusTitle ? -1 : undefined}
        className={cn(
          autoFocusTitle && 'focus:outline-none',
          'font-display text-sl-ink',
          page
            ? 'mt-6 text-[32px] font-semibold leading-[1.1] md:text-[42px]'
            : 'mt-4 text-[21px] font-medium leading-tight md:text-[23px]'
        )}
      >
        {title}
      </Heading>
      {description && (
        <div
          className={cn(
            'mt-3 max-w-[540px] font-sans text-sl-muted',
            page ? 'text-[15.5px] leading-[1.6]' : 'text-[14.5px] leading-[1.55]'
          )}
        >
          {description}
        </div>
      )}
      {children && (
        <div className={cn('flex w-full flex-col items-center', page ? 'mt-7' : 'mt-5')}>
          {children}
        </div>
      )}
    </div>
  );
}
